import { Button, Logo } from '@viaja/design-system';
import { useEffect, useRef, useState } from 'react';
import poster from './assets/cris-boas-vindas-poster.jpg';
import video from './assets/cris-boas-vindas.mp4';

export function LoginPreloader({
  active,
  ready,
  onComplete,
}: {
  active: boolean;
  ready: boolean;
  onComplete: () => void;
}) {
  const media = useRef<HTMLVideoElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [ended, setEnded] = useState(false);
  const [source, setSource] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(preference.matches);
    preference.addEventListener('change', update);
    return () => preference.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    setSource(undefined);
    setFailed(false);
    if (reducedMotion) return;
    const controller = new AbortController();
    let objectUrl: string | undefined;
    // Carrega o arquivo completo antes de reproduzir: Safari não depende de Range HTTP.
    void fetch(video, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Vídeo indisponível.');
        return response.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        if (!blob.size || !blob.type.startsWith('video/')) throw new Error('Mídia inválida.');
        objectUrl = URL.createObjectURL(blob);
        setSource(objectUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!active) return;
    if (reducedMotion) {
      onComplete();
      return;
    }
    heading.current?.focus();
    // Mídia/rede lenta nunca bloqueiam o acesso além deste limite.
    const deadline = window.setTimeout(onComplete, 9_000);
    return () => window.clearTimeout(deadline);
  }, [active, reducedMotion, onComplete]);

  useEffect(() => {
    if (!active || reducedMotion) return;
    if (failed) {
      onComplete();
      return;
    }
    if (!source) return;
    let cancelled = false;
    media.current?.play().catch(() => {
      if (!cancelled) onComplete();
    });
    return () => {
      cancelled = true;
    };
  }, [active, reducedMotion, failed, source, onComplete]);

  useEffect(() => {
    if (active && ready && ended) onComplete();
  }, [active, ready, ended, onComplete]);

  return (
    <main className="app-screen app-screen--bare app-login-preloader" hidden={!active}>
      <Logo variant="wordmark" height={44} />
      <div className="app-login-preloader-media" aria-hidden="true">
        {!reducedMotion ? (
          <video
            ref={media}
            src={source}
            poster={poster}
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            tabIndex={-1}
            onEnded={() => setEnded(true)}
            onError={() => active && onComplete()}
          />
        ) : null}
      </div>
      <div className="app-stack app-login-preloader-copy">
        <h1 ref={heading} className="display-section" tabIndex={-1}>
          Preparando suas viagens
        </h1>
        <output className="body app-meta" aria-live="polite" aria-atomic="true">
          {ready ? 'Vamos nessa.' : 'Carregando rotas e alertas…'}
        </output>
      </div>
      <Button variant="quiet" onClick={onComplete}>
        Abrir app
      </Button>
    </main>
  );
}
